/**
 * MenuDecoder Component — Translate menu items and convert prices (refined)
 */
import React, { useState } from 'react';
import { Languages, Sparkles } from 'lucide-react';
import { Modal, Spinner, ErrorMessage } from '../../shared';
import { menuApi } from '../../../services/api';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' }
];

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' }
];

export function MenuDecoder({ isOpen, onClose, restaurant, dishes, t }) {
  const [menuText, setMenuText] = useState('');
  const [targetLang, setTargetLang] = useState('en');
  const [targetCurrency, setTargetCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const parseMenuText = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => {
      const priceMatch = line.match(/(\d+)\s*(?:THB|฿|B)?\s*$/i);
      return {
        name: priceMatch ? line.replace(priceMatch[0], '').trim() : line.trim(),
        price: priceMatch ? parseInt(priceMatch[1]) : 0
      };
    });
  };

  const handleTranslate = async () => {
    setLoading(true);
    setError(null);

    try {
      const items = dishes?.length > 0
        ? dishes.map(d => ({ name: d.name, price: d.price, isSignature: d.isSignature }))
        : parseMenuText(menuText);

      if (items.length === 0) {
        setError(new Error('Please enter menu items to translate'));
        setLoading(false);
        return;
      }

      const response = await menuApi.decode(items, targetLang, targetCurrency, restaurant?.id);
      setResults(response);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const useRestaurantDishes = () => {
    if (dishes?.length > 0) {
      setMenuText(dishes.map(d => `${d.name} ${d.price}฿`).join('\n'));
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Menu Decoder"
      icon="🍽️"
      size="lg"
    >
      <div className="space-y-4">
        {!results ? (
          <>
            <div className="flex gap-2.5">
              <div className="flex-1">
                <label className="text-[11px] text-stone-500 mb-1.5 block font-semibold uppercase tracking-wider">Language</label>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  className="w-full bg-stone-100 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 text-stone-700 transition-all duration-200"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-[11px] text-stone-500 mb-1.5 block font-semibold uppercase tracking-wider">Currency</label>
                <select
                  value={targetCurrency}
                  onChange={(e) => setTargetCurrency(e.target.value)}
                  className="w-full bg-stone-100 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 text-stone-700 transition-all duration-200"
                >
                  {CURRENCIES.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] text-stone-500 mb-1.5 block font-semibold uppercase tracking-wider">Menu Items (one per line)</label>
              <textarea
                value={menuText}
                onChange={(e) => setMenuText(e.target.value)}
                placeholder={"ไข่เจียวปู 1000\nผัดขี้เมา 500\nผัดไทย 400"}
                className="w-full bg-stone-100 rounded-xl px-3.5 py-3 text-sm h-28 resize-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:bg-white text-stone-700 placeholder:text-stone-400 transition-all duration-200"
              />
            </div>

            {dishes?.length > 0 && !menuText && (
              <button
                onClick={useRestaurantDishes}
                className="text-[11px] text-primary-500 hover:text-primary-600 font-semibold transition-colors"
              >
                Use restaurant menu ({dishes.length} items)
              </button>
            )}

            <button
              onClick={handleTranslate}
              disabled={loading || (!menuText && !dishes?.length)}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.97]"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="text-white" />
                  Translating...
                </>
              ) : (
                <>
                  <Languages size={16} />
                  Translate Menu
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="space-y-2">
              {results.items?.map((item, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-xl border transition-all duration-200 ${item.isMustTry || item.isSignature
                      ? 'bg-amber-50/60 border-amber-100'
                      : 'bg-warm-50 border-stone-100'
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-stone-400 font-medium">{item.original}</p>
                      <p className="font-semibold text-stone-800 text-sm">{item.translated}</p>
                      {item.description && (
                        <p className="text-[11px] text-stone-400 mt-0.5">{item.description}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-stone-800">
                        {item.price.convertedSymbol}{item.price.converted}
                      </p>
                      <p className="text-[10px] text-stone-400">
                        ฿{item.price.original}
                      </p>
                    </div>
                  </div>
                  {(item.isMustTry || item.isSignature) && (
                    <div className="flex items-center gap-1 mt-2 text-amber-600 text-[11px] font-semibold">
                      <Sparkles size={11} />
                      Must Try
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-3 bg-warm-50 rounded-xl text-[11px] text-stone-400 font-medium">
              <p>Exchange rate: 1 THB = {results.exchangeRate?.toFixed(4)} {targetCurrency}</p>
              <p>Source: {results.dataSource === 'database' ? 'Cached' : 'AI Translation'}</p>
            </div>

            <button
              onClick={() => setResults(null)}
              className="w-full text-sm text-primary-500 hover:text-primary-600 font-semibold py-2 transition-colors"
            >
              Translate another menu
            </button>
          </>
        )}

        {error && <ErrorMessage error={error} onRetry={handleTranslate} />}
      </div>
    </Modal>
  );
}

export default MenuDecoder;
